import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Badge } from './ui/badge';
import { AlertTriangle, TrendingUp, DollarSign, Clock, Activity } from 'lucide-react';

// Risk level configuration
const RISK_CONFIG = {
  LOW_RISK: {
    emoji: '🟢',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
    label: 'Liquidité élevée',
    labelEn: 'High Liquidity'
  },
  MODERATE_RISK: {
    emoji: '🟡',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30',
    label: 'Liquidité modérée',
    labelEn: 'Moderate Liquidity'
  },
  HIGH_RISK: {
    emoji: '🟠',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500/30',
    label: 'Liquidité faible',
    labelEn: 'Low Liquidity'
  },
  VERY_HIGH_RISK: {
    emoji: '🔴',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/30',
    label: 'Très illiquide',
    labelEn: 'Very Illiquid'
  }
};

// Format large numbers
const formatUSD = (value) => {
  if (!value || value === 0) return '$0';
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
};

/**
 * Compact liquidity badge for screener table
 */
export const LiquidityBadgeCompact = ({ score, riskLevel }) => {
  const config = RISK_CONFIG[riskLevel] || RISK_CONFIG.MODERATE_RISK;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.color} ${config.borderColor} border cursor-help`}>
            <span>{config.emoji}</span>
            <span>{score}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
          <div className="text-sm">
            <p className="font-medium">{config.label}</p>
            <p className="text-gray-400 text-xs mt-1">
              Score de liquidité: {score}/100
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Compact max position display for screener table
 */
export const MaxPositionCompact = ({ conservative, moderate }) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-gray-300 cursor-help text-sm">
            {formatUSD(conservative)}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
          <div className="text-sm space-y-1">
            <p className="font-medium">Guide de position</p>
            <p className="text-emerald-400">Conservateur: {formatUSD(conservative)}</p>
            <p className="text-yellow-400">Modéré: {formatUSD(moderate)}</p>
            <p className="text-gray-400 text-xs mt-1">
              Basé sur le volume moyen 30j
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Full liquidity score component for stock detail page
 */
export const LiquidityScoreCard = ({ data }) => {
  if (!data) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-400">
          <Activity className="w-5 h-5" />
          <span>Données de liquidité non disponibles</span>
        </div>
      </div>
    );
  }
  
  const {
    score_overall,
    risk_level,
    volume_30d_usd,
    spread_bps_estimated,
    turnover_ratio,
    max_position_conservative_usd,
    max_position_moderate_usd,
    max_position_aggressive_usd,
    max_position_stress_usd,
    estimated_exit_days,
    last_calculated_at
  } = data;
  
  const config = RISK_CONFIG[risk_level] || RISK_CONFIG.MODERATE_RISK;
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-400" />
          <h3 className="text-lg font-semibold text-white">Score de Liquidité</h3>
        </div>
        <Badge className={`${config.bgColor} ${config.color} ${config.borderColor} border`}>
          {config.emoji} {score_overall}/100
        </Badge>
      </div>
      
      {/* Risk Level Banner */}
      <div className={`p-3 rounded-lg ${config.bgColor} ${config.borderColor} border`}>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{config.emoji}</span>
          <div>
            <p className={`font-medium ${config.color}`}>{config.label}</p>
            <p className="text-gray-400 text-sm">Sortie estimée: {estimated_exit_days} jours</p>
          </div>
        </div>
      </div>
      
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white/5 rounded p-3">
          <div className="flex items-center gap-1 text-gray-400 text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            <span>Volume 30j</span>
          </div>
          <p className="text-white font-medium">{formatUSD(volume_30d_usd)}</p>
        </div>
        
        <div className="bg-white/5 rounded p-3">
          <div className="flex items-center gap-1 text-gray-400 text-sm mb-1">
            <Activity className="w-4 h-4" />
            <span>Spread</span>
          </div>
          <p className="text-white font-medium">{spread_bps_estimated} bps</p>
        </div>
        
        <div className="bg-white/5 rounded p-3">
          <div className="flex items-center gap-1 text-gray-400 text-sm mb-1">
            <Clock className="w-4 h-4" />
            <span>Turnover</span>
          </div>
          <p className="text-white font-medium">{(turnover_ratio * 100).toFixed(2)}%</p>
        </div>
      </div>
      
      {/* Position Sizing Guide */}
      <div className="border-t border-white/10 pt-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-5 h-5 text-amber-400" />
          <h4 className="text-white font-medium">Guide de Position</h4>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center p-2 bg-emerald-500/10 rounded">
            <div>
              <span className="text-emerald-400 font-medium">Conservateur</span>
              <span className="text-gray-400 text-sm ml-2">(sortie 2j)</span>
            </div>
            <span className="text-white font-medium">{formatUSD(max_position_conservative_usd)}</span>
          </div>
          
          <div className="flex justify-between items-center p-2 bg-yellow-500/10 rounded">
            <div>
              <span className="text-yellow-400 font-medium">Modéré</span>
              <span className="text-gray-400 text-sm ml-2">(sortie 5j)</span>
            </div>
            <span className="text-white font-medium">{formatUSD(max_position_moderate_usd)}</span>
          </div>
          
          <div className="flex justify-between items-center p-2 bg-orange-500/10 rounded">
            <div>
              <span className="text-orange-400 font-medium">Agressif</span>
              <span className="text-gray-400 text-sm ml-2">(sortie 10j)</span>
            </div>
            <span className="text-white font-medium">{formatUSD(max_position_aggressive_usd)}</span>
          </div>
          
          <div className="flex justify-between items-center p-2 bg-red-500/10 rounded border border-red-500/30">
            <div className="flex items-center gap-1">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 font-medium">Stress</span>
              <span className="text-gray-400 text-sm ml-1">(marché -20%)</span>
            </div>
            <span className="text-white font-medium">{formatUSD(max_position_stress_usd)}</span>
          </div>
        </div>
        
        <p className="text-gray-500 text-xs mt-3">
          * Basé sur 1% du volume quotidien pour sortie conservative. 
          Scénario stress: liquidité -70%.
        </p>
      </div>
      
      {/* Last Updated */}
      {last_calculated_at && (
        <p className="text-gray-500 text-xs text-right">
          Mis à jour: {new Date(last_calculated_at).toLocaleDateString('fr-FR')}
        </p>
      )}
    </div>
  );
};

export default LiquidityScoreCard;
